/**
 * Shared utility functions for JelenJS
 */
import {ReadonlySignal, Signal, isSignalGetter} from "./signal";
import { DEBUG_MODE, debugLog } from './debug';

// Re-export DEBUG for backward compatibility
export const DEBUG = DEBUG_MODE;

/**
 * Helper to detect if an object is a signal
 */
export function isSignal(value: any): boolean {
    // Signal detection for the new implementation
    if (value === null || value === undefined) {
        return false;
    }

    if (DEBUG_MODE) {
        debugLog(`[Signal] isSignal check for:`, value, typeof value);
        
        if (typeof value === 'function') {
            debugLog(`[Signal] Is function:`, value);
            debugLog(`[Signal] isSignalGetter check:`, isSignalGetter(value));
        }
        
        if (Array.isArray(value) && value.length === 2 && 
            typeof value[0] === 'function' && typeof value[1] === 'function') {
            debugLog(`[Signal] Is tuple:`, value);
        }
    }

    // Check if it's a signal tuple [getter, setter]
    if (Array.isArray(value) && value.length === 2 &&
        typeof value[0] === 'function' && typeof value[1] === 'function') {
        return true;
    }

    // Check if it's a getter function marked as a signal getter
    if (isSignalGetter(value)) {
        return true;
    }

    return false;
}

/**
 * Generate a unique ID
 */
export function generateId(prefix: string = ''): string {
    return prefix + Math.random().toString(36).substr(2, 9);
}

/**
 * Helper to safely get value from a signal
 * Works with both getter functions and signal tuples
 * For class attributes, converts boolean value to string ('active' or '')
 */
export function getSignalValue<T>(signal: Signal<T> | ReadonlySignal<T> | T, isClassAttr: boolean = false): T {
    if (DEBUG_MODE) debugLog(`[Signal] getSignalValue() called with:`, signal, typeof signal);
    
    let rawValue: any;

    if (typeof signal === 'function') {
        try {
            // Try to get the value by calling the function
            rawValue = (signal as ReadonlySignal<T>)();
            if (DEBUG_MODE) debugLog(`[Signal] Function call result:`, rawValue);
        } catch (e) {
            // If it's not callable as a signal, use the raw value
            if (DEBUG_MODE) debugLog(`[Signal] Function call failed:`, e);
            rawValue = signal;
        }
    } else if (Array.isArray(signal) && typeof signal[0] === 'function') {
        try {
            // Try to get the value from the first element of the tuple
            rawValue = signal[0]();
            if (DEBUG_MODE) debugLog(`[Signal] Tuple[0] call result:`, rawValue);
        } catch (e) {
            // If it fails, use the raw array
            if (DEBUG_MODE) debugLog(`[Signal] Tuple[0] call failed:`, e);
            rawValue = signal;
        }
    } else {
        if (DEBUG_MODE) debugLog(`[Signal] Using raw value:`, signal);
        rawValue = signal as T;
    }

    // For class attributes, convert boolean to string value
    if (isClassAttr && typeof rawValue === 'boolean') {
        return (rawValue ? 'active' : '') as unknown as T;
    }

    return rawValue;
}

/**
 * Helper for using signals directly in JSX
 * This is a convenience function that ensures signals are properly tracked in JSX
 *
 * Usage: <div>{useSignal(count)}</div> instead of <div>{count}</div>
 */
export function useSignal<T>(signal: Signal<T> | ReadonlySignal<T> | T): T {
    if (DEBUG_MODE) debugLog('[Signal] useSignal called with:', signal);

    if (typeof signal === 'function') {
        try {
            const result = (signal as ReadonlySignal<T>)();
            if (DEBUG_MODE) debugLog('[Signal] useSignal function result:', result);
            return result;
        } catch (e) {
            if (DEBUG_MODE) debugLog('[Signal] useSignal function call failed:', e);
            return signal as T;
        }
    } else if (Array.isArray(signal) && typeof signal[0] === 'function') {
        try {
            const result = signal[0]();
            if (DEBUG_MODE) debugLog('[Signal] useSignal tuple[0] result:', result);
            return result;
        } catch (e) {
            if (DEBUG_MODE) debugLog('[Signal] useSignal tuple[0] call failed:', e);
            return signal as T;
        }
    }

    if (DEBUG_MODE) debugLog('[Signal] useSignal returning raw value:', signal);
    return signal as T;
}
